// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ProjectTableColumn } from '@/project/components/project-table';
import {
  Paper,
  Skeleton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
} from '@mui/material';

const DEFAULT_COLUMNS: ProjectTableColumn[] = [
  'integration',
  'name',
  'timeline',
  'workflow-stage',
  'customer',
  'locations',
  'assets',
  'billing-summary',
];

const SX: Partial<Record<ProjectTableColumn, SxProps<Theme>>> = {
  integration: { width: '3rem', px: 2, whiteSpace: 'nowrap' },
  timeline: { minWidth: '20rem', maxWidth: '24rem' },
  'workflow-stage': {
    display: { xs: 'none', lg: 'table-cell' },
    width: '12rem',
  },
  customer: { display: { xs: 'none', sm: 'table-cell' } },
  locations: { display: { xs: 'none', md: 'table-cell' } },
  assets: { display: { xs: 'none', sm: 'table-cell' } },
  'billing-summary': {
    display: { xs: 'none', lg: 'table-cell' },
    width: '14rem',
  },
};

interface Props {
  /**
   * A list of columns to display in the skeleton.
   * @default DEFAULT_COLUMNS
   */
  columns?: ProjectTableColumn[];
}

/**
 * A skeleton loading state for the ProjectTable component.
 * Matches the structure and layout of the actual table.
 */
export function ProjectTableSkeleton({ columns = DEFAULT_COLUMNS }: Props) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((column) => {
              const sx = SX[column];
              return (
                <TableCell key={column} sx={sx}>
                  <Skeleton variant="text" width={100} />
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {[1, 2, 3, 4, 5].map((row) => (
            <TableRow key={row}>
              {columns.map((column) => {
                const sx = SX[column];
                return (
                  <TableCell key={column} sx={sx}>
                    <Skeleton variant="text" />
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
